import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {PreparedBlock} from '@github-ui/taillights-worker/document'
import {startTransition, useEffect, useLayoutEffect, useReducer, type RefObject} from 'react'
import {useTaillightContext} from './taillight-context'
import {bindTaillightText} from './taillight-dom'
import type {TaillightProps} from './taillight-types'
import {useTaillightsController, useTaillightsCSSScope} from './taillights-provider'

function registerPaint(
  scope: ReturnType<typeof useTaillightsCSSScope>,
  element: HTMLElement | null,
  block: PreparedBlock,
  fail: (error: Error) => void,
) {
  try {
    return scope.register(bindTaillightText(element, block.text), block, block.palette.colors)
  } catch (failure) {
    const nextError = failure instanceof Error ? failure : new Error(String(failure))
    startTransition(() => fail(nextError))
  }
}

/**
 * Binds prepared syntax ranges to native text and reports binding failures without replacing that text. With
 * `beforePaint`, ranges register before the browser paints, so text that was already colored never paints plain.
 */
function CSSPaint({
  block,
  paintRef,
  beforePaint,
}: {
  block: PreparedBlock
  paintRef: RefObject<HTMLElement | null>
  beforePaint: boolean
}) {
  const [error, fail] = useReducer((_previous: Error | null, next: Error) => next, null)
  const scope = useTaillightsCSSScope()
  useLayoutEffect(
    () => (beforePaint ? registerPaint(scope, paintRef.current, block, fail) : undefined),
    [beforePaint, block, paintRef, scope],
  )
  useEffect(
    () => (beforePaint ? undefined : registerPaint(scope, paintRef.current, block, fail)),
    [beforePaint, block, paintRef, scope],
  )
  if (error) throw error
  return null
}

/** Highlights native text with CSS ranges, preserving its DOM and controls without requesting bitmap paint. */
export function TaillightCSS(props: TaillightProps) {
  const {block} = props
  const controller = useTaillightsController()
  const {nativeSourceRef} = useTaillightContext()
  return controller && block.paint !== 'none' ? (
    <ErrorBoundary key={props.block.imageKey} boundaryName="TaillightCSS" fallback={null} onError={props.onRenderError}>
      <CSSPaint block={block} paintRef={nativeSourceRef} beforePaint={props.fallbackDirectives !== undefined} />
    </ErrorBoundary>
  ) : null
}
